import { apiClient } from "./health";

export type AppRole = "VIEWER" | "UPLOADER" | "ADMIN";
export const OPERATIONAL_ROLE_OPTIONS = ["VIEWER", "ADMIN"] as const;

export interface AuthenticatedUser {
  id: string;
  username: string;
  display_name: string;
  role: AppRole;
  must_change_password: boolean;
  session_expires_at: string;
}

export interface ManagedUser {
  username: string;
  display_name: string;
  role: AppRole;
  is_active: boolean;
  active_sessions: number;
  last_login_at: string | null;
  created_at: string;
}

interface AuthenticationResponse {
  user: AuthenticatedUser;
}

export const getCurrentUser = (signal?: AbortSignal) =>
  apiClient.get<AuthenticationResponse>("/api/auth/me", { signal });

export const signIn = (username: string, password: string) =>
  apiClient.request<AuthenticationResponse>("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

export const signOut = () =>
  apiClient.request<{ status: string }>("/api/auth/logout", { method: "POST" });

export const changePassword = (currentPassword: string, newPassword: string) =>
  apiClient.request<{ status: string }>("/api/auth/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });

export const listManagedUsers = () =>
  apiClient.get<{ users: ManagedUser[] }>("/api/auth/admin/users");

export const createManagedUser = (payload: {
  username: string;
  display_name: string;
  password: string;
  role: AppRole;
  must_change_password: boolean;
}) => apiClient.request<{ user: ManagedUser }>("/api/auth/admin/users", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(payload),
});

export const updateManagedUser = (
  username: string,
  payload: { role?: AppRole; is_active?: boolean },
) => apiClient.request<{ status: string }>(`/api/auth/admin/users/${encodeURIComponent(username)}`, {
  method: "PATCH",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(payload),
});

export const resetManagedUserPassword = (
  username: string,
  password: string,
) => apiClient.request<{ status: string }>(`/api/auth/admin/users/${encodeURIComponent(username)}/reset-password`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ password, must_change_password: true }),
});

export const revokeManagedUserSessions = (username: string) =>
  apiClient.request<{ status: string; revoked: number }>(`/api/auth/admin/users/${encodeURIComponent(username)}/revoke-sessions`, {
    method: "POST",
  });

export function roleAllows(actual: AppRole, minimum: AppRole): boolean {
  if (actual === "ADMIN") return true;
  return (actual === "VIEWER" || actual === "UPLOADER") && minimum === "VIEWER";
}
